<script setup>
import {computed, onBeforeMount, ref} from "vue";

const props = defineProps({
  fundName: String,
  asAtDate: String,
  fundData: Object,
  hasBenchmark: Boolean,
  blockData: Object
})

const selectedPerformanceData = ref(props.blockData.fund_data[0])
const performanceData1 = ref([])
const performanceData2 = ref([])
const performanceAttributes = ref({
  yearly: {
    labelAttributes: ['year_one_label', 'year_two_label', 'year_three_label', 'year_four_label', 'year_five_label'],
    dataAttributes: ['year_one', 'year_two', 'year_three', 'year_four', 'year_five']
  },
  annualised: {
    labelAttributes: ['quarter_label', 'one_year_label', 'three_year_label', 'five_year_label'],
    dataAttributes: ['quarter', 'one_year', 'three_year', 'five_year']
  }
})

const getLayoutClass = computed(() => {
  return {
    'col-12 block': props.hasBenchmark,
    'col-12 col-md-6 block no-benchmark': !props.hasBenchmark
  }
})
const boolIsYearlyPerformanceData = computed(() => {
  return (props.fundData.standard[0].year_one != null ||
      props.fundData.standard[0].year_two != null ||
      props.fundData.standard[0].year_three != null ||
      props.fundData.standard[0].year_four != null ||
      props.fundData.standard[0].year_five != null);
})
onBeforeMount(() => {
  let myNewData = [];

  // Collates fund performance, equity performance and
  // benchmark data
  myNewData.push(props.blockData.fund_data[0])
  for (let i = 0; i < props.blockData.equities.length; i++) {
    myNewData.push(props.blockData.equities[i].standard_data[0]);
  }
  myNewData.push(props.blockData.fund_data[1])

  // Split data into an array for each
  // of the two tables in output order
  for (let equityIndex = 0; equityIndex < myNewData.length; equityIndex++) {
    let equitySet1 = [];
    let equitySet2 = [];
    Object.keys(myNewData[equityIndex]).forEach(function (key, index) {
      let value = myNewData[equityIndex][key];
      if (index > 5) {
        equitySet1.unshift(value);
      }
      if (index > 1 && index < 6) {
        equitySet2.push(value);
      }
    });
    performanceData1.value.push(equitySet1);
    performanceData2.value.push(equitySet2);
  }
})
</script>

<template>
  <section>
    <div class="row">
      <div v-if="boolIsYearlyPerformanceData" v-bind:class="getLayoutClass">
        <div class="table-header" v-html="props.blockData.header_year"></div>

        <table class="table performance-table print-hide">
          <thead>
          <tr v-if="props.blockData.equities === undefined || props.blockData.equities.length > 0">
            <th colspan="3" class="text-center">
              <select v-model="selectedPerformanceData">
                <option :value="props.blockData.fund_data[0]">{{ props.fundName }}</option>
                <option v-for="(equity, index) in props.blockData.equities"
                        :value="props.blockData.equities[index].standard_data[0]">{{ equity.name }}
                </option>
              </select>
            </th>
          </tr>
          <tr>
            <th class="performance-table_cell--period">Period</th>
            <th class="text-center">Return</th>
            <th v-if="props.hasBenchmark" class="text-center">Benchmark</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, index) in performanceAttributes.yearly.dataAttributes">
            <td>{{ props.fundData.standard[0][performanceAttributes.yearly.labelAttributes[index]] }}</td>
            <td>
              <transition name="slide-fade" mode="out-in">
                                    <span
                                        :key="selectedPerformanceData[performanceAttributes.yearly.dataAttributes[index]]"
                                        class="text-right">{{
                                        selectedPerformanceData[performanceAttributes.yearly.dataAttributes[index]]
                                      }}</span>
              </transition>
            </td>
            <td v-if="props.hasBenchmark"><span class="text-right">{{
                props.fundData.benchmark[0][performanceAttributes.yearly.dataAttributes[index]]
              }}</span>
            </td>
          </tr>
          </tbody>
        </table>

        <table class="table performance-table print-show">
          <thead>
          <tr>
            <th>Period</th>
            <th>{{ props.fundName }}</th>
            <th v-for="(equity, index) in props.blockData.equities">{{ equity.name }}</th>
            <th v-if="props.hasBenchmark">Benchmark</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, index) in performanceAttributes.yearly.dataAttributes">
            <td>{{ props.fundData.standard[0][performanceAttributes.yearly.labelAttributes[index]] }}</td>
            <td>{{ props.fundData.standard[0][performanceAttributes.yearly.dataAttributes[index]] }}</td>
            <td v-if="props.blockData.equities === undefined || props.blockData.equities.length > 0"
                v-for="(fund, n) in props.blockData.equities">
                                <span class="text-right">{{
                                    props.blockData.equities[n].standard_data[0][performanceAttributes.yearly.dataAttributes[index]]
                                  }}</span>
            </td>
            <td v-if="props.hasBenchmark">{{
                props.fundData.benchmark[0][performanceAttributes.yearly.dataAttributes[index]]
              }}
            </td>
          </tr>
          </tbody>
        </table>

        <div class="table-footer" v-html="props.blockData.footer_year"></div>
      </div>

      <div v-bind:class="getLayoutClass">
        <div class="table-header" v-html="props.blockData.header_past"></div>

        <table class="table performance-table print-hide">
          <thead>
          <tr v-if="props.blockData.equities === undefined || props.blockData.equities.length > 0">
            <th v-if="props.hasBenchmark" colspan="3" class="text-center">
              <select v-model="selectedPerformanceData">
                <option :value="props.blockData.fund_data[0]">{{ props.fundName }}</option>
                <option v-for="(equity, index) in props.blockData.equities"
                        :value="props.blockData.equities[index].standard_data[0]">{{ equity.name }}
                </option>
              </select>
            </th>
          </tr>
          <tr>
            <th class="performance-table_cell--period">Period</th>
            <th class="text-center">Return</th>
            <th v-if="props.hasBenchmark" class="text-center">Benchmark</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, index) in performanceAttributes.annualised.dataAttributes">
            <td>{{ props.fundData.standard[0][performanceAttributes.annualised.labelAttributes[index]] }}</td>
            <td>
              <transition name="slide-fade" mode="out-in">
                                    <span
                                        :key="selectedPerformanceData[performanceAttributes.annualised.dataAttributes[index]]"
                                        class="text-right">{{
                                        selectedPerformanceData[performanceAttributes.annualised.dataAttributes[index]]
                                      }}</span>
              </transition>
            </td>
            <td v-if="props.hasBenchmark"><span class="text-right">{{
                props.fundData.benchmark[0][performanceAttributes.annualised.dataAttributes[index]]
              }}</span></td>
          </tr>
          </tbody>
        </table>

        <table class="table performance-table print-show">
          <thead>
          <tr>
            <th>Period</th>
            <th>{{ props.fundName }}</th>
            <th v-for="(equity, index) in props.blockData.equities">{{ equity.name }}</th>
            <th v-if="props.hasBenchmark">Benchmark</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, index) in performanceAttributes.annualised.dataAttributes">
            <td>{{ props.fundData.standard[0][performanceAttributes.annualised.labelAttributes[index]] }}</td>
            <td>{{ props.fundData.standard[0][performanceAttributes.annualised.dataAttributes[index]] }}</td>
            <td v-if="props.blockData.equities === undefined || props.blockData.equities.length > 0"
                v-for="(fund, n) in props.blockData.equities">
                                <span class="text-right">{{
                                    props.blockData.equities[n].standard_data[0][performanceAttributes.annualised.dataAttributes[index]]
                                  }}</span>
            </td>
            <td v-if="props.hasBenchmark">{{
                props.fundData.benchmark[0][performanceAttributes.annualised.dataAttributes[index]]
              }}
            </td>
          </tr>
          </tbody>
        </table>

        <div class="table-footer" v-html="props.blockData.footer_past"></div>
      </div>
    </div>

  </section>
</template>

<style lang="scss" scoped>
.block {
  margin-bottom: 1em;
}

select {
  color: black;
}

.table.performance-table {
  margin-bottom: 0;

  td span.text-right {
    display: block;
    padding-right: 40%;
  }

  .performance-table_cell--period {
    min-width: 40%;
  }
}

.no-benchmark {
  .table.performance-table {
    .performance-table_cell--period {
      min-width: 50%;
    }
  }
}

.slide-fade-enter-active {
  transition: all .2s ease;
}

.slide-fade-leave-active {
  transition: all .2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  // transform: rotate(10deg);
  opacity: 0;
}

.table-header,
.table-footer {
  p {
    margin: 0;
  }
}
</style>
